import { Switch, Slider, Select, Typography, Space, Divider, Button, message } from 'antd';
import useSceneStore from '../../stores/sceneStore';

const { Text } = Typography;

interface ParticlePreset {
  label: string;
  config: Partial<ReturnType<typeof useSceneStore.getState>['effects']['particles']>;
}

const presets: ParticlePreset[] = [
  {
    label: '降雨',
    config: {
      emitter: 'rain', emitterSize: 80, count: 5000, lifetime: 3,
      speed: 15, speedVariation: 3, size: 0.3, sizeVariation: 0.1,
      color: '#aaccee', colorEnd: '#6699cc', blendMode: 'normal',
      gravity: [0, -15, 0], spread: 5, offset: [0, 30, 0],
    },
  },
  {
    label: '暴雪',
    config: {
      emitter: 'snow', emitterSize: 80, count: 4000, lifetime: 8,
      speed: 2, speedVariation: 1, size: 0.6, sizeVariation: 0.3,
      color: '#ffffff', colorEnd: '#ddeeff', blendMode: 'normal',
      gravity: [0, -2, 0], spread: 30, offset: [0, 30, 0],
    },
  },
  {
    label: '风场',
    config: {
      emitter: 'flow', emitterSize: 60, count: 2000, lifetime: 4,
      speed: 8, speedVariation: 2, size: 0.4, sizeVariation: 0.2,
      color: '#66ffaa', colorEnd: '#00ff88', blendMode: 'additive',
      gravity: [0, 0, 0], spread: 20, offset: [-30, 5, 0],
    },
  },
  {
    label: '空气污染扩散',
    config: {
      emitter: 'sphere', emitterSize: 5, count: 3000, lifetime: 10,
      speed: 1.5, speedVariation: 0.8, size: 1.2, sizeVariation: 0.5,
      color: '#ff8800', colorEnd: '#ff4400', blendMode: 'additive',
      gravity: [0, 1, 0], spread: 360, offset: [0, 2, 0],
    },
  },
  {
    label: '交通流量',
    config: {
      emitter: 'flow', emitterSize: 40, count: 2000, lifetime: 6,
      speed: 5, speedVariation: 2, size: 0.3, sizeVariation: 0.15,
      color: '#00aaff', colorEnd: '#ffaa00', blendMode: 'additive',
      gravity: [0, 0, 0], spread: 10, offset: [-20, 0.5, 0],
    },
  },
  {
    label: '火焰',
    config: {
      emitter: 'cone', emitterSize: 3, count: 2000, lifetime: 2,
      speed: 6, speedVariation: 2, size: 1.0, sizeVariation: 0.4,
      color: '#ffcc00', colorEnd: '#ff2200', blendMode: 'additive',
      gravity: [0, 2, 0], spread: 25, offset: [0, 0, 0],
    },
  },
  {
    label: '烟雾',
    config: {
      emitter: 'cone', emitterSize: 2, count: 1500, lifetime: 6,
      speed: 2, speedVariation: 1, size: 2.0, sizeVariation: 0.8,
      color: '#888888', colorEnd: '#333333', blendMode: 'normal',
      gravity: [0, 1.5, 0], spread: 15, offset: [0, 0, 0],
    },
  },
  {
    label: '萤火虫',
    config: {
      emitter: 'sphere', emitterSize: 20, count: 500, lifetime: 8,
      speed: 0.5, speedVariation: 0.3, size: 0.6, sizeVariation: 0.3,
      color: '#ffff44', colorEnd: '#aaff00', blendMode: 'additive',
      gravity: [0, 0.3, 0], spread: 360, offset: [0, 3, 0],
    },
  },
];

export default function ParticlePanel() {
  const rawParticles = useSceneStore((s) => s.effects.particles);
  const setEffects = useSceneStore((s) => s.setEffects);
  // 2026-08-31｜防御：旧场景粒子配置缺字段（speedVariation 等）会导致面板 toFixed(undefined) 崩溃
  const particles = {
    ...{ size: 0.8, speed: 3, speedVariation: 0, count: 500, lifetime: 5, emitterSize: 30, spread: 360, offset: [0, 0, 0] as number[], gravity: [0, 0, 0] as number[] },
    ...(rawParticles || {}),
  };

  const update = (patch: Partial<typeof particles>) => {
    setEffects({ particles: { ...particles, ...patch } });
  };

  const applyPreset = (preset: ParticlePreset) => {
    setEffects({
      particles: {
        ...particles,
        ...preset.config,
        enabled: true,
      },
    });
  };

  return (
    <div style={{ padding: '8px 0' }}>
      <Space orientation="vertical" style={{ width: '100%' }} size={12}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text>启用粒子</Text>
          <Switch checked={particles.enabled} onChange={(v) => update({ enabled: v })} />
        </div>

        {particles.enabled && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Text type="secondary" style={{ fontSize: 11 }}>场景预设（点击自动配置）</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {presets.map((p) => (
                <div
                  key={p.label}
                  onClick={() => applyPreset(p)}
                  style={{
                    padding: '2px 8px',
                    fontSize: 11,
                    border: '1px solid #444',
                    borderRadius: 4,
                    cursor: 'pointer',
                    color: '#ddd',
                    background: '#2a2a3c',
                  }}
                >
                  {p.label}
                </div>
              ))}
            </div>

            <Divider style={{ margin: '4px 0' }} />

            <div>
              <Text type="secondary">发射器</Text>
              <Select
                value={particles.emitter}
                onChange={(v) => update({ emitter: v })}
                options={[
                  { value: 'point', label: '点' },
                  { value: 'sphere', label: '球体' },
                  { value: 'box', label: '立方体' },
                  { value: 'cone', label: '锥体' },
                  { value: 'ring', label: '环形' },
                  { value: 'rain', label: '雨滴（向下）' },
                  { value: 'snow', label: '雪花（向下）' },
                  { value: 'flow', label: '流动（水平）' },
                ]}
                size="small"
                style={{ width: '100%', marginTop: 4 }}
              />
            </div>

            <div>
              <Text type="secondary">发射器大小: {particles.emitterSize.toFixed(0)}</Text>
              <Slider
                min={1}
                max={1000}
                step={1}
                value={particles.emitterSize}
                onChange={(v) => update({ emitterSize: v })}
              />
            </div>

            <div>
              {/* 适配场景范围：主流编辑器（Unity/UE 粒子bounds）可将粒子区域一键扩展到场景尺寸，
                  避免大厂区模型上粒子只覆盖一小块 */}
              <Button
                size="small"
                block
                onClick={() => {
                  const sceneRadius = useSceneStore.getState().environment.sceneRadius;
                  const target = Math.min(1000, Math.max(200, Math.round(sceneRadius * 1.5)));
                  update({ emitterSize: target });
                  message.success(`发射器区域已设为 ${target}（约覆盖半径 ${sceneRadius.toFixed(0)} 的场景）`);
                }}
              >
                适配场景范围（当前 {Math.round(useSceneStore.getState().environment.sceneRadius)}）
              </Button>
            </div>

            <div>
              <Text type="secondary">粒子数量: {particles.count}</Text>
              <Slider
                min={100}
                max={20000}
                step={100}
                value={particles.count}
                onChange={(v) => update({ count: v })}
              />
            </div>

            <div>
              <Text type="secondary">生命周期: {particles.lifetime.toFixed(1)}s</Text>
              <Slider
                min={0.5}
                max={30}
                step={0.5}
                value={particles.lifetime}
                onChange={(v) => update({ lifetime: v })}
              />
            </div>

            <div>
              <Text type="secondary">速度: {particles.speed.toFixed(1)}</Text>
              <Slider
                min={0}
                max={30}
                step={0.5}
                value={particles.speed}
                onChange={(v) => update({ speed: v })}
              />
            </div>

            <div>
              <Text type="secondary">速度变化: {particles.speedVariation.toFixed(1)}</Text>
              <Slider
                min={0}
                max={10}
                step={0.1}
                value={particles.speedVariation}
                onChange={(v) => update({ speedVariation: v })}
              />
            </div>

            <div>
              <Text type="secondary">大小: {particles.size.toFixed(2)}</Text>
              <Slider
                min={0.05}
                max={5}
                step={0.05}
                value={particles.size}
                onChange={(v) => update({ size: v })}
              />
            </div>

            <div>
              <Text type="secondary">扩散角度: {particles.spread}°</Text>
              <Slider
                min={1}
                max={360}
                step={1}
                value={particles.spread}
                onChange={(v) => update({ spread: v })}
              />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Text type="secondary">起始色</Text>
                <input
                  type="color"
                  value={particles.color}
                  onChange={(e) => update({ color: e.target.value })}
                  style={{ width: '100%', height: 28, border: 'none', cursor: 'pointer', marginTop: 4 }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Text type="secondary">结束色</Text>
                <input
                  type="color"
                  value={particles.colorEnd}
                  onChange={(e) => update({ colorEnd: e.target.value })}
                  style={{ width: '100%', height: 28, border: 'none', cursor: 'pointer', marginTop: 4 }}
                />
              </div>
            </div>

            <div>
              <Text type="secondary">混合模式</Text>
              <Select
                value={particles.blendMode}
                onChange={(v) => update({ blendMode: v })}
                options={[
                  { value: 'additive', label: '叠加（发光）' },
                  { value: 'normal', label: '正常' },
                ]}
                size="small"
                style={{ width: '100%', marginTop: 4 }}
              />
            </div>

            <div>
              <Text type="secondary">重力 Y: {particles.gravity[1].toFixed(1)}</Text>
              <Slider
                min={-30}
                max={30}
                step={0.5}
                value={particles.gravity[1]}
                onChange={(v) => update({ gravity: [particles.gravity[0], v, particles.gravity[2]] })}
              />
            </div>

            <Divider style={{ margin: '4px 0' }} />
            <Text type="secondary" style={{ fontSize: 11 }}>发射位置偏移</Text>

            <div>
              <Text type="secondary">X: {particles.offset[0].toFixed(0)}</Text>
              <Slider
                min={-100}
                max={100}
                step={1}
                value={particles.offset[0]}
                onChange={(v) => update({ offset: [v, particles.offset[1], particles.offset[2]] })}
              />
            </div>
            <div>
              <Text type="secondary">Y: {particles.offset[1].toFixed(0)}</Text>
              <Slider
                min={-50}
                max={100}
                step={1}
                value={particles.offset[1]}
                onChange={(v) => update({ offset: [particles.offset[0], v, particles.offset[2]] })}
              />
            </div>
            <div>
              <Text type="secondary">Z: {particles.offset[2].toFixed(0)}</Text>
              <Slider
                min={-100}
                max={100}
                step={1}
                value={particles.offset[2]}
                onChange={(v) => update({ offset: [particles.offset[0], particles.offset[1], v] })}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text>循环</Text>
              <Switch checked={particles.loop} onChange={(v) => update({ loop: v })} />
            </div>
          </>
        )}
      </Space>
    </div>
  );
}
